import { useAsyncState } from '@vueuse/core';
import LacunaWebPKI, { CertificateModel, Promise as WebPkiPromise } from 'web-pki';

import { env } from '~~core';

type GenericCallBack = () => void;
type UseWebPkiOptions = {
  onNotInstalled?: GenericCallBack;
  onBrowserNotSupported?: GenericCallBack;
  onOutdated?: GenericCallBack;
  onCertificatesLoad?: (certificates: CertificateModel[]) => void;
};

export const enum InstallationStates {
  NOT_INSTALLED = 1,
  BROWSER_NOT_SUPPORTED = 3,
  OUTDATED = 2,
  INSTALLED = 0,
}

export const promisifyWebPki = <T extends WebPkiPromise<any>, K = T extends WebPkiPromise<infer U> ? U : never>(webPkiPromise: T) => new Promise<K>((resolve, reject) => {
  webPkiPromise.success(resolve).fail(reject);
});

export function useWebPki(options: UseWebPkiOptions = {}) {
  const pkiLicense = env.LACUNA_WEB_PKI_LICENSE;
  const pki = new LacunaWebPKI(pkiLicense);

  function onNotInstalled(status: LacunaWebPKI.InstallationStates) {
    const callbacks: Record<LacunaWebPKI.InstallationStates, GenericCallBack | undefined> = {
      [InstallationStates.NOT_INSTALLED]: options.onNotInstalled,
      [InstallationStates.BROWSER_NOT_SUPPORTED]: options.onBrowserNotSupported,
      [InstallationStates.OUTDATED]: options.onOutdated,
      [InstallationStates.INSTALLED]: undefined,
    };

    const callback = callbacks[status];

    callback?.();
  }

  function redirectToInstallPage() {
    pki.redirectToInstallPage();
  }

  function init() {
    // There is a bug that if you call .success it will call the success callback when the ext is not installed
    return new Promise<void>((resolve, reject) => {
      pki.init({
        ready: resolve,
        notInstalled: onNotInstalled,
      }).fail(reject);
    });
  }

  function listCertificates() {
    return promisifyWebPki(pki.listCertificates());
  }

  function readCertificate(thumbprint: string) {
    return promisifyWebPki(pki.readCertificate({ thumbprint }));
  }

  const { isLoading, state: certificates, error } = useAsyncState(async () => {
    await init();

    return listCertificates();
  }, [], { onSuccess: options.onCertificatesLoad });

  function signHashBatch(certificateThumbprint: string, batch: string[], digestAlgorithm: string) {
    return promisifyWebPki(
      pki.signHashBatch({ certificateThumbprint, batch, digestAlgorithm }),
    );
  }

  return {
    isLoading,
    certificates,
    error,
    redirectToInstallPage,
    readCertificate,
    signHashBatch,
  };
}
